import { useState, useEffect, useRef, useCallback } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Download, FileText, Calendar, ImageIcon,
  Plus, Trash2, Camera, Library, X, CheckSquare, Square,
  GripVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { VoiceInputButton } from "@/components/voice-input-button";
import { compressImageSrc } from "@/lib/image-compression";
import { ProtokollPDFGenerator, type ProtokollData } from "@/lib/protokoll-pdf-generator";
import type { Project, Photo } from "@shared/schema";

// ── Data structures ─────────────────────────────────────────────────────────

interface StepPhoto {
  localId: string;
  /** Always stored as a proper data: URL */
  dataUrl: string;
  fileName: string;
  description: string;
  /** Set when the photo originated from the project library */
  projectPhotoId?: number;
}

interface Schritt {
  id: string;
  text: string;
  photos: StepPhoto[];
}

/** v2 persisted format */
interface StoredProtokollDataV2 {
  version: 2;
  protokollDate: string;
  schritte: Schritt[];
}

/** v1 legacy format (single text + photo IDs) */
interface StoredProtokollDataV1 {
  protokollText?: string;
  protokollDate?: string;
  selectedPhotoIds?: number[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function makeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Converts raw base64 (from DB) or existing data URL to a valid data: URL. */
function toDataUrl(raw: string, mimeType: string): string {
  if (!raw) return "";
  if (raw.startsWith("data:")) return raw;
  return `data:${mimeType};base64,${raw}`;
}

function makeEmptySchritt(): Schritt {
  return { id: makeId(), text: "", photos: [] };
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ProjectProtokollPage() {
  const [, params] = useRoute("/project/:id/protokoll");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const projectId = params?.id ? parseInt(params.id) : null;

  // ── Server data ────────────────────────────────────────────────────────────
  const { data: project } = useQuery<Project>({
    queryKey: [`/api/projects/${projectId}`],
    enabled: !!projectId,
  });

  // Lightweight metadata only (no base64) — used to show/hide the library button.
  const { data: photoMeta = [] } = useQuery<{ id: number }[]>({
    queryKey: [`/api/projects/${projectId}/photos`],
    enabled: !!projectId,
  });

  // Full photo data (with base64) — fetched lazily, only when the library picker is opened.
  const [libraryFetchEnabled, setLibraryFetchEnabled] = useState(false);
  const { data: libraryPhotos = [], isFetching: libraryLoading } = useQuery<Photo[]>({
    queryKey: [`/api/projects/${projectId}/photos/full`],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${projectId}/photos/full`, { credentials: "include" });
      if (!res.ok) throw new Error("Fehler beim Laden der Fotos");
      return res.json();
    },
    enabled: libraryFetchEnabled && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 min — don't re-fetch while the dialog stays open
  });

  // ── Form state ─────────────────────────────────────────────────────────────
  const [protokollDate, setProtokollDate] = useState(todayIso());
  const [schritte, setSchritte] = useState<Schritt[]>([makeEmptySchritt()]);
  const [initialized, setInitialized] = useState(false);

  // Hydrate from server once — runs as soon as project loads, no photo data needed.
  useEffect(() => {
    if (initialized || !project) return;
    const raw = project.protokollData as StoredProtokollDataV2 | StoredProtokollDataV1 | null | undefined;
    if (raw) {
      if ((raw as StoredProtokollDataV2).version === 2) {
        const v2 = raw as StoredProtokollDataV2;
        if (v2.protokollDate) setProtokollDate(v2.protokollDate);
        if (v2.schritte?.length) setSchritte(v2.schritte);
      } else {
        // Migrate v1 → v2: convert single text to one Schritt.
        // Photos are not migrated (they can be re-added via the library picker).
        const v1 = raw as StoredProtokollDataV1;
        if (v1.protokollDate) setProtokollDate(v1.protokollDate);
        setSchritte([{ id: makeId(), text: v1.protokollText ?? "", photos: [] }]);
      }
    }
    setInitialized(true);
  }, [project, initialized]);

  // ── Autosave ───────────────────────────────────────────────────────────────
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");

  const scheduleSave = useCallback(
    (date: string, steps: Schritt[]) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        if (!projectId) return;
        setSaveState("saving");
        try {
          const payload: StoredProtokollDataV2 = {
            version: 2,
            protokollDate: date,
            schritte: steps,
          };
          await apiRequest("PATCH", `/api/projects/${projectId}/protokoll`, {
            protokollData: payload,
          });
          setSaveState("saved");
          setTimeout(() => setSaveState("idle"), 1500);
        } catch {
          setSaveState("idle");
        }
      }, 800);
    },
    [projectId],
  );

  useEffect(() => {
    if (!initialized) return;
    scheduleSave(protokollDate, schritte);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protokollDate, schritte, initialized]);

  // ── Schritt mutations ──────────────────────────────────────────────────────
  const updateSchrittText = (id: string, text: string) => {
    setSchritte((prev) => prev.map((s) => (s.id === id ? { ...s, text } : s)));
  };

  const addSchritt = () => {
    setSchritte((prev) => [...prev, makeEmptySchritt()]);
    // Scroll to bottom after render
    setTimeout(() => window.scrollBy({ top: 400, behavior: "smooth" }), 100);
  };

  const removeSchritt = (id: string) => {
    setSchritte((prev) => prev.filter((s) => s.id !== id));
  };

  const removePhotoFromSchritt = (schrittId: string, localId: string) => {
    setSchritte((prev) =>
      prev.map((s) =>
        s.id === schrittId
          ? { ...s, photos: s.photos.filter((p) => p.localId !== localId) }
          : s,
      ),
    );
  };

  // ── File upload (camera / gallery) ────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadForStepRef = useRef<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const triggerUpload = (schrittId: string) => {
    uploadForStepRef.current = schrittId;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      const targetId = uploadForStepRef.current;
      if (!files.length || !targetId) return;
      setUploading(true);
      try {
        const newPhotos: StepPhoto[] = await Promise.all(
          files.map(async (file) => {
            const dataUrl = await new Promise<string>((res, rej) => {
              const reader = new FileReader();
              reader.onload = (ev) => res(ev.target?.result as string);
              reader.onerror = rej;
              reader.readAsDataURL(file);
            });
            // Compress to max 1200px, 0.8 quality
            let compressed = dataUrl;
            try {
              compressed = await compressImageSrc(dataUrl, 1200, 0.8);
            } catch { /* use original */ }
            return {
              localId: makeId(),
              dataUrl: compressed,
              fileName: file.name,
              description: "",
            };
          }),
        );
        setSchritte((prev) =>
          prev.map((s) =>
            s.id === targetId ? { ...s, photos: [...s.photos, ...newPhotos] } : s,
          ),
        );
      } finally {
        setUploading(false);
      }
    },
    [],
  );

  // ── Library picker dialog ──────────────────────────────────────────────────
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const [libraryForStep, setLibraryForStep] = useState<string | null>(null);
  const [librarySelection, setLibrarySelection] = useState<Set<number>>(new Set());

  const openLibraryPicker = (schrittId: string) => {
    const schritt = schritte.find((s) => s.id === schrittId);
    const alreadyAdded = new Set(
      (schritt?.photos ?? [])
        .filter((p) => p.projectPhotoId !== undefined)
        .map((p) => p.projectPhotoId!),
    );
    setLibrarySelection(new Set(alreadyAdded));
    setLibraryForStep(schrittId);
    setLibraryFetchEnabled(true); // trigger full-photo fetch only now
    setLibraryDialogOpen(true);
  };

  const toggleLibraryPhoto = (id: number) => {
    setLibrarySelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyLibrarySelection = () => {
    if (!libraryForStep) return;
    const schritt = schritte.find((s) => s.id === libraryForStep);
    const existingLibIds = new Set(
      (schritt?.photos ?? [])
        .filter((p) => p.projectPhotoId !== undefined)
        .map((p) => p.projectPhotoId!),
    );

    // Photos to add (newly selected)
    const toAdd: StepPhoto[] = [];
    librarySelection.forEach((pid) => {
      if (existingLibIds.has(pid)) return;
      const p = libraryPhotos.find((lp) => lp.id === pid);
      if (!p) return;
      const rawSrc = (p as any).annotatedData || p.data || "";
      const dataUrl = toDataUrl(rawSrc, p.mimeType);
      if (!dataUrl) return;
      toAdd.push({
        localId: makeId(),
        dataUrl,
        fileName: p.fileName,
        description: p.description ?? "",
        projectPhotoId: p.id,
      });
    });

    // Photos to remove (deselected)
    const toRemove = new Set<string>();
    (schritt?.photos ?? []).forEach((sp) => {
      if (sp.projectPhotoId !== undefined && !librarySelection.has(sp.projectPhotoId)) {
        toRemove.add(sp.localId);
      }
    });

    setSchritte((prev) =>
      prev.map((s) => {
        if (s.id !== libraryForStep) return s;
        const filtered = s.photos.filter((p) => !toRemove.has(p.localId));
        return { ...s, photos: [...filtered, ...toAdd] };
      }),
    );
    setLibraryDialogOpen(false);
  };

  // ── PDF preview ────────────────────────────────────────────────────────────
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  const hasContent = schritte.some((s) => s.text.trim() || s.photos.length > 0);

  const previewDepKey = JSON.stringify({
    d: protokollDate,
    schritte: schritte.map((s) => ({
      t: s.text,
      p: s.photos.map((ph) => ph.localId),
    })),
    pn: project?.name,
    cu: project?.customer,
    ad: project?.address,
  });

  useEffect(() => {
    if (!project || !hasContent) {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const data: ProtokollData = buildProtokollData(project, protokollDate, schritte);
        const generator = new ProtokollPDFGenerator();
        const blob = await generator.generateProtokollPDF(data);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        console.error("Protokoll-Vorschau-Fehler:", err);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 700);
    return () => { cancelled = true; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDepKey]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  // ── Download PDF ───────────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownload = async () => {
    if (!project) return;
    setIsGenerating(true);
    try {
      const data: ProtokollData = buildProtokollData(project, protokollDate, schritte);
      const generator = new ProtokollPDFGenerator();
      const blob = await generator.generateProtokollPDF(data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dateStr = protokollDate.replace(/-/g, "");
      a.href = url;
      a.download = `Leistungsprotokoll_${dateStr}_${project.name.replace(/[^a-zA-Z0-9äöüÄÖÜ]/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "PDF heruntergeladen", description: "Leistungsprotokoll wurde erstellt." });
    } catch {
      toast({ variant: "destructive", title: "Fehler", description: "PDF konnte nicht erstellt werden." });
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (!project) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500">Projekt wird geladen…</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hidden file input for camera/gallery */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-bauwerk-teal mb-1">{project.name}</h1>
          <p className="text-gray-600 text-sm">Leistungsprotokoll</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => navigate(`/project/edit/${projectId}`)}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zum Projekt
          </Button>
          <Button
            onClick={handleDownload}
            disabled={isGenerating || !hasContent}
            className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"
          >
            <Download className="w-4 h-4 mr-2" />
            {isGenerating ? "Erstelle PDF…" : "PDF herunterladen"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left: Editor */}
        <div className="space-y-4">
          {/* Datum */}
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-4 flex-wrap">
                <Label htmlFor="protokoll-date" className="flex items-center gap-1.5 text-sm font-medium shrink-0">
                  <Calendar className="w-4 h-4 text-bauwerk-teal" />
                  Protokolldatum
                </Label>
                <Input
                  id="protokoll-date"
                  type="date"
                  value={protokollDate}
                  onChange={(e) => setProtokollDate(e.target.value)}
                  className="max-w-xs"
                />
                <div className="ml-auto text-xs text-gray-500">
                  {saveState === "saving" && <span className="text-gray-400">Speichert…</span>}
                  {saveState === "saved" && <span className="text-bauwerk-teal">✓ Gespeichert</span>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Arbeitsschritte */}
          {schritte.map((schritt, idx) => (
            <Card key={schritt.id} className="border-l-4 border-l-bauwerk-teal">
              <CardHeader className="pb-3">
                <CardTitle className="text-bauwerk-teal flex items-center justify-between">
                  <span className="flex items-center gap-2 text-base">
                    <GripVertical className="w-4 h-4 text-gray-300" />
                    {schritte.length > 1 ? `Arbeitsschritt ${idx + 1}` : "Leistungsbeschreibung"}
                  </span>
                  {schritte.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-red-500 -mr-2"
                      onClick={() => removeSchritt(schritt.id)}
                      title="Schritt entfernen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Text + Voice */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">
                      {schritte.length > 1 ? `Beschreibung Schritt ${idx + 1}` : "Erbrachte Leistungen"}
                    </Label>
                    <VoiceInputButton
                      value={schritt.text}
                      onChange={(val) => updateSchrittText(schritt.id, val)}
                      cleanupContext="protokoll"
                      label="Diktieren"
                    />
                  </div>
                  <Textarea
                    placeholder={
                      idx === 0
                        ? "z. B. Vorbereitung der Fassade, Reinigung mit HD-Wasser, Risse aufweiten und verschließen, …"
                        : "Beschreiben Sie die erbrachten Leistungen dieses Schritts …"
                    }
                    value={schritt.text}
                    onChange={(e) => updateSchrittText(schritt.id, e.target.value)}
                    rows={6}
                    className="text-sm resize-none"
                  />
                </div>

                {/* Foto-Bereich */}
                <div>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <ImageIcon className="w-4 h-4 text-bauwerk-teal" />
                      Fotos
                    </span>
                    <div className="flex gap-2 ml-auto flex-wrap">
                      {/* Camera / gallery upload */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint text-xs"
                        onClick={() => triggerUpload(schritt.id)}
                        disabled={uploading}
                      >
                        <Camera className="w-3.5 h-3.5 mr-1" />
                        Foto aufnehmen / hochladen
                      </Button>
                      {/* Library picker */}
                      {photoMeta.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-gray-300 text-gray-600 hover:bg-gray-50 text-xs"
                          onClick={() => openLibraryPicker(schritt.id)}
                        >
                          <Library className="w-3.5 h-3.5 mr-1" />
                          Aus Bibliothek ({photoMeta.length})
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Photo grid for this step */}
                  {schritt.photos.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {schritt.photos.map((photo) => (
                        <div key={photo.localId} className="relative group">
                          <img
                            src={photo.dataUrl}
                            alt={photo.fileName}
                            className="w-full aspect-square object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removePhotoFromSchritt(schritt.id, photo.localId)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                            title="Foto entfernen"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                          {photo.description && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 rounded-b-lg px-1 py-0.5">
                              <p className="text-white text-[9px] truncate">{photo.description}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center text-xs text-gray-400">
                      Noch keine Fotos – Kamera oder Bibliothek nutzen
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Schritt hinzufügen */}
          <Button
            variant="outline"
            className="w-full border-dashed border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint"
            onClick={addSchritt}
          >
            <Plus className="w-4 h-4 mr-2" />
            Weiteren Arbeitsschritt hinzufügen
          </Button>
        </div>

        {/* Right: PDF Preview */}
        <div>
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="text-bauwerk-teal flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  PDF-Vorschau
                </span>
                {previewLoading && (
                  <span className="text-xs font-normal text-gray-400 animate-pulse">Aktualisiert…</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!hasContent ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Fügen Sie einen Text oder Fotos hinzu, um die Vorschau zu sehen.
                </div>
              ) : previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="w-full rounded-b-lg border-0"
                  style={{ height: "calc(100vh - 200px)", minHeight: "600px" }}
                  title="Protokoll-Vorschau"
                />
              ) : (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {previewLoading ? "Vorschau wird erstellt…" : "Keine Vorschau verfügbar."}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Library picker dialog */}
      <Dialog open={libraryDialogOpen} onOpenChange={setLibraryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-bauwerk-teal">Fotos aus Projektbibliothek wählen</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1">
            {libraryPhotos.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">Noch keine Fotos im Projekt.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 py-2">
                {libraryPhotos.map((photo) => {
                  const selected = librarySelection.has(photo.id);
                  const rawSrc = (photo as any).annotatedData || photo.data;
                  const src = rawSrc ? toDataUrl(rawSrc, photo.mimeType) : "";
                  return (
                    <button
                      key={photo.id}
                      type="button"
                      onClick={() => toggleLibraryPhoto(photo.id)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        selected
                          ? "border-bauwerk-teal ring-2 ring-bauwerk-teal/30"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {src ? (
                        <img
                          src={src}
                          alt={photo.fileName}
                          className="w-full aspect-square object-cover"
                        />
                      ) : (
                        <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-300" />
                        </div>
                      )}
                      <div
                        className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center shadow ${
                          selected ? "bg-bauwerk-teal" : "bg-white/80"
                        }`}
                      >
                        {selected ? (
                          <CheckSquare className="w-3.5 h-3.5 text-white" />
                        ) : (
                          <Square className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </div>
                      {photo.description && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5">
                          <p className="text-white text-[9px] truncate">{photo.description}</p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter className="pt-3 border-t">
            <span className="text-xs text-gray-500 mr-auto">
              {librarySelection.size} ausgewählt
            </span>
            <Button variant="outline" onClick={() => setLibraryDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              className="bg-bauwerk-teal hover:bg-bauwerk-teal/90"
              onClick={applyLibrarySelection}
            >
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Helper: build ProtokollData for PDF generator ──────────────────────────

function buildProtokollData(
  project: Project,
  protokollDate: string,
  schritte: Schritt[],
): ProtokollData {
  return {
    projectName: project.name,
    customerName: project.customer ?? "",
    projectAddress: project.address ?? "",
    protokollDate,
    schritte: schritte
      .filter((s) => s.text.trim() || s.photos.length > 0)
      .map((s) => ({
        text: s.text,
        photos: s.photos.map((p) => ({
          dataUrl: p.dataUrl,
          fileName: p.fileName,
          description: p.description,
        })),
      })),
  };
}
